"use client";

/**
 * SmbPlansDropdown (EthnicPath) - the "Small Business" pricing section, mirrored
 * from the Ethos Virtual marketing site. Sits under the main Business /
 * Enterprise cards on the pricing section.
 *
 * SMB plans (entitlements):
 *   Starter      50 included min  · 1 language · incoming calls only
 *   Standard     150 included min · (Most popular) · optional two-way add-on
 *   High-Volume  300 included min
 *
 * TWO-CURRENCY: prices, overage rates, yearly totals, and checkout links come
 * from src/lib/constants/pricing-display.ts in the currency chosen by the
 * on-page CA$ / US$ toggle (passed down as `currency`). CAD is native (live
 * hardcoded Stripe links); USD checkout links are env-driven and currently
 * absent, so USD CTAs gracefully fall back to a contact page. Do not hardcode
 * dollar amounts here.
 *
 * No setup fee on any Small Business tier. Styling reuses the host repo's global
 * classes (`pricing-card`, `btn-primary`) and CSS custom properties.
 */

import { motion } from "framer-motion";
import { Check } from "lucide-react";
import {
  contactHref,
  contactLabel,
  formatMoney,
  formatRate,
  getPlanPricing,
  YEARLY_DISCOUNT_PCT,
  resolveCheckoutUrl,
  type BillingPeriod,
  type Currency,
} from "@/lib/constants/pricing-display";

export type SmbPlanKey = "starter" | "standard" | "high-volume";

export interface SmbPlan {
  key: SmbPlanKey;
  name: string;
  includedMinutes: number;
  maxLanguages: number;
  inboundOnly?: boolean;
  /** Whether the plan offers the optional two-way calling add-on (Standard). */
  hasTwoWayAddOn?: boolean;
  blurb: string;
  featured?: boolean;
}

export const SMB_PLANS: readonly SmbPlan[] = [
  {
    key: "starter",
    name: "Starter",
    includedMinutes: 50,
    maxLanguages: 1,
    inboundOnly: true,
    blurb: "For small teams getting started with voice AI.",
  },
  {
    key: "standard",
    name: "Standard",
    includedMinutes: 150,
    maxLanguages: 2,
    hasTwoWayAddOn: true,
    blurb: "For growing teams that need more conversations.",
    featured: true,
  },
  {
    key: "high-volume",
    name: "High-Volume",
    includedMinutes: 300,
    maxLanguages: 2,
    blurb: "For busy teams with steady call volume.",
  },
];

/** The Standard two-way add-on monthly price, per currency. */
const TWO_WAY_ADDON: Record<Currency, number> = { CAD: 100, USD: 60 };

function planFeatures(plan: SmbPlan, currency: Currency): string[] {
  const overage = getPlanPricing(currency, plan.key).overage;
  const features = [
    `Includes ${plan.includedMinutes.toLocaleString(
      currency === "USD" ? "en-US" : "en-CA"
    )} minutes per month`,
    `${formatRate(overage)} ${currency} per additional minute`,
    "No setup fee",
    "Up to 1 voice agent",
    `Up to ${plan.maxLanguages} language${plan.maxLanguages === 1 ? "" : "s"}`,
  ];
  if (plan.inboundOnly) features.push("Incoming calls only");
  if (plan.hasTwoWayAddOn) {
    features.push(
      `Optional two-way calling (inbound + outbound dialing): +${formatMoney(
        TWO_WAY_ADDON[currency],
        currency
      )} ${currency}/mo, up to 2 agents`
    );
  }
  return features;
}

export interface SmbPlansDropdownProps {
  /** Monthly or yearly display; the toggle lives in Pricing.tsx. */
  billingPeriod?: BillingPeriod;
  /** Currency selected by the host's CA$ / US$ toggle. */
  currency?: Currency;
  id?: string;
}

function PlanCard({
  plan,
  billingPeriod,
  currency,
}: {
  plan: SmbPlan;
  billingPeriod: BillingPeriod;
  currency: Currency;
}) {
  const pricing = getPlanPricing(currency, plan.key);
  const yearly = billingPeriod === "yearly";
  // Resolve the live checkout link for this currency+interval; null -> contact
  // fallback so a USD price is never sent to a CAD checkout.
  const url = resolveCheckoutUrl(currency, plan.key, billingPeriod);
  const href = url ?? contactHref(currency);
  const isExternal = href.startsWith("http");
  const ctaLabel = url ? `Choose ${plan.name}` : contactLabel(currency);
  const displayedPerMonth = yearly ? pricing.yearlyPerMonth : pricing.price;

  return (
    <div
      className="pricing-card"
      style={{
        display: "flex",
        flexDirection: "column",
        textAlign: "center",
        ...(plan.featured
          ? { borderColor: "var(--color-accent)", boxShadow: "0 8px 32px rgba(0, 0, 0, 0.08), 0 0 20px rgba(100, 180, 255, 0.45)" }
          : {}),
      }}
    >
      {plan.featured && (
        <span style={{ alignSelf: "center", display: "inline-block", fontFamily: "var(--font-body)", fontSize: "0.72rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--color-accent)", marginBottom: 12 }}>
          Most popular
        </span>
      )}

      <h3 style={{ fontFamily: "var(--font-heading)", fontSize: "1.5rem", color: "var(--color-text)", marginBottom: 4 }}>{plan.name}</h3>
      <p style={{ fontSize: "0.88rem", color: "var(--color-text-muted)", fontWeight: 300, lineHeight: 1.5, marginBottom: 20, minHeight: 40 }}>{plan.blurb}</p>

      <div style={{ marginBottom: 8 }}>
        <span style={{ fontFamily: "var(--font-heading)", fontSize: "2.8rem", color: "var(--color-text)", lineHeight: 1 }}>{formatMoney(displayedPerMonth, currency)}</span>
        <span style={{ fontSize: "0.88rem", color: "var(--color-text-muted)", fontWeight: 300, marginLeft: 6 }}>{currency} / month</span>
      </div>
      <p style={{ fontSize: "0.75rem", color: "var(--color-text-muted)", fontWeight: 300, marginBottom: 24, paddingBottom: 24, borderBottom: "1px solid var(--color-border)" }}>
        {yearly
          ? `${formatMoney(pricing.yearlyTotal, currency)} ${currency} billed annually (save ${YEARLY_DISCOUNT_PCT}%) · no setup fee`
          : "Billed monthly · no setup fee"}
      </p>

      <ul style={{ listStyle: "none", padding: 0, marginBottom: 32, textAlign: "left" }}>
        {planFeatures(plan, currency).map((f) => (
          <li key={f} style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 12, fontSize: "0.88rem", color: "var(--color-text)", fontWeight: 400, lineHeight: 1.5 }}>
            <Check size={16} style={{ flexShrink: 0, marginTop: 3 }} color={plan.featured ? "var(--color-accent)" : "var(--color-text-muted)"} strokeWidth={2} />
            {f}
          </li>
        ))}
      </ul>

      <a
        href={href}
        {...(isExternal ? { target: "_blank", rel: "noopener noreferrer" } : {})}
        className="btn-primary"
        style={{ marginTop: "auto", width: "100%", justifyContent: "center", background: plan.featured ? "var(--color-text)" : "transparent", color: plan.featured ? "#fff" : "var(--color-text)", border: plan.featured ? "none" : "1.5px solid var(--color-border)", padding: "16px 24px" }}
      >
        {ctaLabel}
      </a>
    </div>
  );
}

export default function SmbPlansDropdown({
  billingPeriod = "monthly",
  currency = "CAD",
  id = "smb-plans",
}: SmbPlansDropdownProps) {
  return (
    <section id={id} aria-label="Small Business plans" style={{ marginBottom: 40 }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 4, textAlign: "center", marginBottom: 32 }}>
        <h3 style={{ fontFamily: "var(--font-heading)", fontSize: "1.5rem", color: "var(--color-text)", margin: 0 }}>Small Business Plans</h3>
        <p style={{ fontSize: "0.88rem", fontWeight: 300, color: "var(--color-text-muted)", margin: 0 }}>Right-sized voice AI for smaller teams. No setup fee. All prices in {currency}.</p>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.3 }}
        style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 24 }}
      >
        {SMB_PLANS.map((plan) => (
          <PlanCard key={plan.key} plan={plan} billingPeriod={billingPeriod} currency={currency} />
        ))}
      </motion.div>
    </section>
  );
}
